import {
    Avatar,
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    formatDate,
    htmlToSlate,
    Icon,
    Pills,
    RichText,
    slateToHtml,
    Text,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { EmployeeNoteCategory, EmployeeNoteDraft } from '@/types/employee-note';
import type { SessionUser } from '@/types/session/SessionUser';

import { ChoiceChips } from '@/components/ChoiceChips/ChoiceChips';
import { EDITOR_TOOLBAR_DENSITY } from '@/constants/RichTextEditor';
import { useAmelioTranslations, useSelectedAppLanguage } from '@/hooks';
import { htmlToPlainText } from '@/utils/htmlSanitizer';
import { getInitials } from '@/utils/user';

/** The writer's identity, as the composer announces it. */
export interface NoteComposerAuthor {
    /**
     * The connected user, for their initials; their name is not shown, `youLabel` is.
     *
     * The session carries no `profilePictureUrl` -- that field belongs to a directory entry -- so
     * the avatar here is initials only, where a note already written shows its author's picture.
     */
    user: SessionUser;
    /** How the writer is named to themselves, e.g. "Vous". */
    youLabel: string;
}

/** The tools this editor offers: the writing ones, without the link and the strikethrough. */
const NOTE_TOOLBAR = { strikethrough: false, link: false } as const;

/** A note whose markup carries no text at all cannot be saved. */
const isNoteEmpty = (html: string) => htmlToPlainText(html).trim() === '';

interface NoteComposerProps {
    /** Markup the editor opens on: the note being edited, or nothing for a new one. */
    initialContent?: string;
    /** The folder the note starts in. */
    category: EmployeeNoteCategory;
    /**
     * The folders the writer may pick from, shown as chips; omit to file under `category`
     * without asking (the growth journey always writes `Career`).
     */
    selectableCategories?: readonly EmployeeNoteCategory[];
    placeholder: string;
    /**
     * Who is writing, shown above the editor with today's date -- for a list read by who wrote what.
     * Omit it where the surrounding card already says whose note this is.
     */
    author?: NoteComposerAuthor;
    onSave: (draft: EmployeeNoteDraft) => void;
    onCancel: () => void;
    /** Hears the note as it is written, or `null` while it holds no text. */
    onDraftChange?: (draft: EmployeeNoteDraft | null) => void;
    isSaving?: boolean;
    dataTestId?: string;
}

/**
 * The rich-text box that writes an employee note, used both to add one and to edit one in place.
 * `RichText` only reads `value` on mount, so the draft lives here and the caller unmounts the
 * composer once the note is saved. Who will read the note is said under the box, and the actions
 * sit as in every form of the module: cancel on the left, save on the right.
 */
export const NoteComposer = ({
    initialContent = '',
    category: initialCategory,
    selectableCategories,
    placeholder,
    author,
    onSave,
    onCancel,
    onDraftChange,
    isSaving = false,
    dataTestId = 'note-composer',
}: NoteComposerProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const [html, setHtml] = useState(initialContent);
    const [category, setCategory] = useState(initialCategory);
    const initialValue = useMemo(() => htmlToSlate(initialContent), [initialContent]);

    const isEmpty = isNoteEmpty(html);

    const changeDraft = (draft: EmployeeNoteDraft) => {
        setHtml(draft.content);
        setCategory(draft.category);
        onDraftChange?.(isNoteEmpty(draft.content) ? null : draft);
    };

    // A note being edited keeps its folder on offer even when the tab cannot file new notes there
    // (a career note opened from the "Notes" tab), so saving never moves it by accident.
    const categoryOptions = selectableCategories
        ? [...new Set([...selectableCategories, initialCategory])].map((value) => ({
              value,
              label: t(`performanceProfile.employeeNotes.categories.${value}`),
          }))
        : null;

    return (
        <div
            className={'flex flex-col gap-3'}
            data-testid={dataTestId}
        >
            {author && (
                <header className={'flex items-start justify-between gap-3'}>
                    <div className={'flex min-w-0 items-center gap-3'}>
                        <Avatar
                            alt={author.youLabel}
                            initials={getInitials(author.user)}
                            bgColor={'gradient'}
                            size={'2xs'}
                        />

                        <Text
                            weight={'semibold'}
                            truncate
                        >
                            {author.youLabel}
                        </Text>
                    </div>

                    <Pills
                        variant={'violet'}
                        size={'sm'}
                    >
                        {formatDate({ date: new Date().toISOString(), language })}
                    </Pills>
                </header>
            )}

            <RichText
                toolbarDensity={EDITOR_TOOLBAR_DENSITY}
                value={initialValue}
                onChange={(value) => changeDraft({ content: slateToHtml(value), category })}
                placeholder={placeholder}
                toolbarOptions={NOTE_TOOLBAR}
                ariaLabel={t('performanceProfile.employeeNotes.editorAria')}
                surface={'glass'}
                contentMinHeight={'compact'}
                locale={language}
                dataTestId={`${dataTestId}-editor`}
            />

            {categoryOptions && (
                <ChoiceChips
                    options={categoryOptions}
                    selected={[category]}
                    onToggle={(nextCategory) =>
                        changeDraft({ content: html, category: nextCategory })
                    }
                    ariaLabel={t('performanceProfile.employeeNotes.categoryAria')}
                    dataTestId={`${dataTestId}-category`}
                />
            )}

            {/* Who will read the note, said with the padlock that marks every confidential mention
                of the module, and set apart from the note itself in italics. */}
            <div className={'flex items-center gap-2'}>
                <Icon
                    name={'security-lock'}
                    size={'sm'}
                    color={Colors.BASE_500}
                    ariaHidden
                />

                <Text
                    size={'small'}
                    color={Colors.BASE_500}
                    className={'italic'}
                >
                    {t('performanceProfile.employeeNotes.visibility')}
                </Text>
            </div>

            <div className={'flex items-center justify-end gap-2'}>
                <ButtonSecondary
                    size={'sm'}
                    fitContent
                    disabled={isSaving}
                    onClick={onCancel}
                    leftIcon={
                        <Icon
                            name={'essential-close'}
                            size={'sm'}
                            ariaHidden
                        />
                    }
                    dataTestId={`${dataTestId}-cancel`}
                >
                    {t('common.cancel')}
                </ButtonSecondary>

                <ButtonGradientPrimary
                    size={'sm'}
                    fitContent
                    loading={isSaving}
                    disabled={isEmpty || isSaving}
                    onClick={() => onSave({ content: html, category })}
                    leftIcon={
                        <Icon
                            name={'essential-checkmark'}
                            size={'sm'}
                            ariaHidden
                        />
                    }
                    dataTestId={`${dataTestId}-save`}
                >
                    {t('performanceProfile.employeeNotes.save')}
                </ButtonGradientPrimary>
            </div>
        </div>
    );
};
